<template>
  <div>
    <!-- ===================== MOBILE CARD LIST (< 960px) ===================== -->
    <div class="d-md-none shipments-mobile-list mt-2 px-2 pb-4">
      <v-progress-linear v-if="loading" indeterminate color="red" class="mb-2" />

      <div
        v-for="(item, index) in shipments"
        :key="item._id"
        class="shipments-mobile-card"
        :class="{
          'shipments-mobile-card--selected': index === activeRow,
        }"
        @click="activeRow = activeRow === index ? null : index"
      >
        <div class="shipments-mobile-card__main">
          <v-checkbox
            :model-value="selected.some(s => s._id === item._id)"
            color="primary"
            hide-details
            density="compact"
            class="flex-shrink-0 mt-1"
            @change.stop="toggleSelection(item)"
            @click.stop
          />

          <div class="shipments-mobile-card__info">
            <div class="shipments-mobile-card__info-top">
              <div
                v-if="isAdminRole"
                class="text-caption text-grey-darken-1"
              >
                {{ $t("shipments.table.qr_code") }}:
                <span class="font-weight-medium">
                  {{ getShipmentQrId(item) }}
                </span>
              </div>

              <div class="font-weight-bold text-primary text-body-2 mt-1">
                {{ item.name }}
              </div>

              <div v-if="item.description" class="text-caption text-grey-darken-1 mt-1">
                {{ item.description }}
              </div>

              <div v-if="getShipmentInstitutionTitle(item)" class="text-caption mt-1">
                <span class="font-weight-medium text-grey-darken-2">
                  {{ $t("shipments.table.institution") }}:
                </span>
                {{ getShipmentInstitutionTitle(item) }}
              </div>
            </div>

            <v-btn
              v-if="item.date"
              color="black"
              variant="outlined"
              size="x-small"
              class="shipments-mobile-card__date mt-2"
              readonly
              @click.stop
            >
              {{ formatDate(item.date) }}
            </v-btn>
          </div>
        </div>

        <div class="shipments-mobile-card__actions">
          <v-btn
            v-if="isAdminRole"
            icon
            variant="text"
            size="small"
            @click.stop="
              openQrDialog({
                id: item._id,
                type: 'shipments',
                printed: item.printed,
              })
            "
          >
            <v-icon size="20" color="grey-darken-2">mdi-qrcode</v-icon>
          </v-btn>

          <v-spacer />

          <v-btn icon variant="text" size="small" @click.stop="insertEmptyParts(item._id)">
            <v-icon size="20" color="grey-darken-2">mdi-plus</v-icon>
          </v-btn>

          <v-btn
            v-if="canEditDelete"
            :disabled="item.vehicle"
            icon
            variant="text"
            size="small"
            @click.stop="editShipment(item)"
          >
            <v-icon size="20" color="grey-darken-2">mdi-pencil</v-icon>
          </v-btn>

          <v-btn
            v-if="canEditDelete"
            :disabled="item.vehicle"
            icon
            variant="text"
            size="small"
            @click.stop="deleteShipment(item._id)"
          >
            <v-icon size="20" color="grey-darken-2">mdi-delete</v-icon>
          </v-btn>
        </div>
      </div>

      <v-alert v-if="!loading && shipments.length < 1" type="info" variant="tonal" class="mt-2">
        No data available.
      </v-alert>
    </div>
    <!-- ===================================================================== -->

    <div class="shipments-table-host pb-2">
      <v-data-table-server
        v-model="selected"
        v-model:options="pagination"
        :headers="headers"
        :items="shipments"
        :items-length="totalItems"
        :items-per-page="pagination.itemsPerPage"
        :items-per-page-options="[
          { value: 5, title: '5' },
          { value: 10, title: '10' },
          { value: 25, title: '25' },
          { value: 50, title: '50' },
        ]"
        items-per-page-text="Rows per page"
        item-value="_id"
        return-object
        show-select
        :loading="loading"
        class="elevation-1 nowrap-headers"
        @update:options="updatePagination"
      >
        <template #progress>
          <v-progress-linear color="red" indeterminate />
        </template>
        <template #item="{ item, index }">
          <tr
            :class="{ tableRowColorSelected: index == activeRow }"
            class="tableRowColor"
            @click="activeRow = activeRow === index ? null : index"
          >
            <td>
              <v-row class="justify-center d-flex pb-3">
                <v-checkbox
                  :model-value="selected.includes(item)"
                  color="primary"
                  hide-details
                  dense
                  @change="toggleSelection(item)"
                />
              </v-row>
            </td>
            <!-----------------------------------------QR CODE----------------------------------------->
            <td
              v-if="
                $store.getters.role === 'LekoAdmin' ||
                $store.getters.role === 'SuperAdmin'
              "
              class="ma-1 pt-1 pb-1"
            >
              <v-row
                class="pointer ma-1 text-center justify-center d-block"
                @click="
                  openQrDialog({
                    id: item._id,
                    type: 'shipments',
                    printed: item.printed,
                  })
                "
              >
                <qr-code :size="60" :text="item._id" />
                <h4>
                  {{ item._id.slice(item._id.length - 6, item._id.length) }}
                </h4>
              </v-row>
            </td>
            <!------------------------------------------NAME------------------------------------------->
            <td class="text-xs">
              {{ item.name }}
            </td>
            <!---------------------------------------DESCRIPTION--------------------------------------->
            <td class="text-xs">
              {{ item.description }}
            </td>
            <!---------------------------------------INSTITUTION--------------------------------------->
            <td class="text-xs">
              {{ item.institution ? item.institution.title : "" }}
            </td>
            <!------------------------------------------DATE------------------------------------------->
            <td class="text-xs">
              <v-btn variant="outlined" small color="black">
                {{ formatDate(item.date) }}
              </v-btn>
            </td>
            <!-----------------------------------------ACTIONS----------------------------------------->
            <td class="justify-center">
              <v-row class="d-flex flex-nowrap pl-2 pr-2">
                <v-btn icon variant="text" @click="insertEmptyParts(item._id)">
                  <v-icon color="grey-darken-2"> mdi-plus </v-icon>
                </v-btn>

                <v-btn
                  v-if="
                    $store.getters.role == 'LekoAdmin' ||
                    $store.getters.role == 'InstitutionAdmin' ||
                    $store.getters.role == 'InstitutionManager' ||
                    $store.getters.role == 'SuperAdmin'
                  "
                  :disabled="item.vehicle"
                  icon
                  variant="text"
                  @click="editShipment(item)"
                >
                  <v-icon color="grey-darken-2"> mdi-pencil </v-icon>
                </v-btn>

                <v-btn
                  v-if="
                    $store.getters.role == 'LekoAdmin' ||
                    $store.getters.role == 'InstitutionAdmin' ||
                    $store.getters.role == 'InstitutionManager' ||
                    $store.getters.role == 'SuperAdmin'
                  "
                  :disabled="item.vehicle"
                  icon
                  variant="text"
                  @click="deleteShipment(item._id)"
                >
                  <v-icon color="grey-darken-2"> mdi-delete </v-icon>
                </v-btn>
              </v-row>
            </td>
            <!----------------------------------------------------------------------------------------->
          </tr>
        </template>
      </v-data-table-server>
    </div>
    <v-dialog v-model="dialogDelete" width="auto">
      <v-card>
        <v-card-title class="text-h6">
          <v-alert class="mb-0" :value="true" type="error">
            {{ $t("delete_alert") }}
          </v-alert>
        </v-card-title>

        <v-card-actions>
          <v-spacer />
          <v-btn text @click="dialogDelete = false">
            {{ $t("close") }}
          </v-btn>
          <v-btn color="red" variant="outlined" @click="confirmDelete">
            {{ $t("delete") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import moment from "moment";
import VueQrCode from "vue-qr-generator";

import getRequest from "../../api/getRequest";
import deleteRequest from "../../api/deleteRequest";

export default {
  components: {
    "qr-code": VueQrCode,
  },
  props: ["search", "institution", "search-by-id"],
  data: () => ({
    headers: [
      {
        title: "Qr Code",
        sortable: false,
        key: "_id",
      },
      {
        title: "Name",
        sortable: false,
        key: "name",
      },
      {
        title: "Description",
        sortable: false,
        key: "description",
      },
      {
        title: "Institution",
        sortable: false,
        key: "institution",
      },
      {
        title: "Date",
        sortable: false,
        key: "date",
      },
    ],
    pagination: {
      itemsPerPage: 5,
      page: 1,
      groupBy: ["date_created"],
      sortBy: { key: "date_created", order: "desc" },
      sortDesc: [true],
    },
    shipments: [],
    totalItems: 0,
    loading: false,
    activeRow: null,
    selected: [],
    shipmentToDelete: null,
    dialogDelete: false,
  }),
  computed: {
    searchField() {
      return this.$props.search;
    },
    isAdminRole() {
      return (
        this.$store.getters.role === "LekoAdmin" ||
        this.$store.getters.role === "SuperAdmin"
      );
    },
    canEditDelete() {
      return (
        this.$store.getters.role == "LekoAdmin" ||
        this.$store.getters.role == "InstitutionAdmin" ||
        this.$store.getters.role == "InstitutionManager" ||
        this.$store.getters.role == "SuperAdmin"
      );
    },
  },
  watch: {
    selected(value) {
      this.$emit("selected", value);
    },
    searchField(value) {
      if (!value) this.getShipments();
      else this.searchShipments(value);
    },
    pagination: {
      handler() {
        if (!this.searchField) this.getShipments();
        else this.searchShipments(this.searchField);
      },
    },
    "$i18n.locale"() {
      this.getLocale();
    },
  },
  created() {
    this.getLocale();
    this.activeRow = null;
  },
  methods: {
    getShipmentQrId(item) {
      if (!item?._id) return "";
      return item._id.slice(item._id.length - 6, item._id.length);
    },
    getShipmentInstitutionTitle(item) {
      return item?.institution ? item.institution.title : "";
    },
    getLocale() {
      this.headers[0].title = this.$t("shipments.table.name");
      this.headers[1].title = this.$t("shipments.table.description");
      this.headers[2].title = this.$t("shipments.table.institution");
      this.headers[3].title = this.$t("shipments.table.date");
      this.headers[4].title = this.$t("shipments.table.actions");
      if (
        this.$store.getters.role === "LekoAdmin" ||
        this.$store.getters.role === "SuperAdmin"
      ) {
        this.headers.splice(0, 0, {
          text: this.$t("shipments.table.qr_code"),
          sortable: false,
          key: "actions",
        });
      }
    },
    getShipments() {
      this.loading = true;
      const sort = this.pagination.sortBy?.[0] || {
        key: "date_created",
        order: "desc",
      };
      getRequest(
        "/shipment/",
        `role=${this.$store.getters.role}&institution_id=${
          this.$store.getters.institutionId
        }&limit=${this.pagination.itemsPerPage}&page=${
          this.pagination.page
        }&sort_by=${sort.key}&descending=${sort.order === "desc"}`,
      )
        .then((response) => {
          this.totalItems = response.count;
          this.shipments = response.shipments;
          this.loading = false;
        })
        .catch(() => {})
        .finally(() => {
          this.loading = false;
        });
    },
    updatePagination(options) {
      this.pagination = {
        ...options,
        itemsPerPage: options.itemsPerPage ? options.itemsPerPage : 5,
        groupBy:
          options.groupBy && options.groupBy.length
            ? options.groupBy
            : ["date_created"],
        sortBy:
          options.sortBy && options.sortBy.length
            ? options.sortBy
            : { key: "date_created", order: "desc" },
        sortDesc:
          options.sortDesc && options.sortDesc.length
            ? options.sortDesc
            : [true],
      };
      this.activeRow = null;
    },
    searchShipments(value) {
      this.loading = true;

      // Build common base params
      const baseParams = [`role=${this.$store.getters.role}`];

      let url = "/shipment/";
      let params = "";
      const sort = this.pagination.sortBy?.[0] || {
        key: "date_created",
        order: "desc",
      };
      if (this.$props.searchById) {
        url = "/shipment/search-by-id";
        params = `search=${value}`;
      } else {
        baseParams.push(`search=${value}`);
        baseParams.push(`institution_id=${this.$store.getters.institutionId}`);
        baseParams.push(`limit=${this.pagination.itemsPerPage}`);
        baseParams.push(`page=${this.pagination.page}`);
        baseParams.push(`sort_by=${sort.key}`);
        baseParams.push(`descending=${sort.order === "desc"}`);
        baseParams.push(`inst=${this.$props.institution}`);

        params = baseParams.join("&");
      }

      getRequest(url, params)
        .then((response) => {
          this.totalItems = response.count;
          this.shipments = response.shipments;
        })
        .catch(() => {})
        .finally(() => {
          this.loading = false;
        });
    },
    openQrDialog(data) {
      this.$emit("qr", data);
    },
    insertEmptyParts(shipmentId) {
      this.$emit("insert", shipmentId);
    },
    editShipment(shipment) {
      this.$emit("edit-shipment", shipment);
    },
    deleteShipment(shipmentId) {
      this.shipmentToDelete = shipmentId;
      this.dialogDelete = true;
    },
    confirmDelete() {
      this.loading = true;
      deleteRequest("/shipment/", `id=${this.shipmentToDelete}`).then(
        (response) => {
          if (response.status === 200) {
            this.shipmentToDelete = null;
            this.dialogDelete = false;
            this.loading = false;
            this.getShipments();
          }
        },
      );
    },
    toggleSelection(item) {
      const index = this.selected.findIndex((s) => s._id === item._id);
      if (index === -1) {
        this.selected.push(item);
      } else {
        this.selected.splice(index, 1);
      }
      this.$emit("selected", this.selected);
    },
    formatDate(date) {
      if (!date) return "";
      moment.locale(localStorage.getItem("language") || "en");
      return moment(date).format("D MMMM YYYY");
    },
  },
};
</script>

<style scoped>
@media (max-width: 959.98px) {
  .shipments-table-host :deep(.v-table__wrapper),
  .shipments-table-host :deep(.v-data-table__wrapper),
  .shipments-table-host :deep(.v-data-table__wrapper__scroll) {
    display: none !important;
  }
  .shipments-table-host :deep(.v-data-table-footer) {
    padding-inline: 12px;
  }
}
@media (min-width: 960px) {
  .shipments-mobile-list {
    display: none !important;
  }
}

.shipments-mobile-card {
  position: relative;
  background: #fff;
  border-radius: 8px;
  margin-bottom: 10px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}
.shipments-mobile-card--selected {
  outline: 2px solid rgb(var(--v-theme-primary));
}
.shipments-mobile-card__main {
  display: flex;
  align-items: flex-start;
  padding: 8px;
  gap: 8px;
}
.shipments-mobile-card__info {
  flex: 1;
  min-width: 0;
}
.shipments-mobile-card__info-top {
  padding-right: 88px;
}
.shipments-mobile-card__date {
  height: auto !important;
  min-height: 0 !important;
  text-transform: none;
  letter-spacing: normal;
}
.shipments-mobile-card__actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  padding: 4px 6px;
}
</style>
